import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Clock3, Plus, RefreshCw, RotateCcw, ShieldCheck, Wrench } from 'lucide-react'
import { Modal } from '../components/Modal'
import { StatusPill } from '../components/StatusPill'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/format'

export function CareLive() {
  const [tasks, setTasks] = useState<any[]>([])
  const [plots, setPlots] = useState<any[]>([])
  const [modal, setModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modalError, setModalError] = useState('')
  const [busy, setBusy] = useState<string | boolean>(false)

  const reload = async () => {
    setLoading(true)
    try {
      const [taskRows, plotRows] = await Promise.all([api.careTasks(), api.plots()])
      setTasks(taskRows)
      setPlots(plotRows)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void reload() }, [])

  const awaitingReview = useMemo(() => tasks.filter((task) => task.status === 'evidence_submitted'), [tasks])
  const open = useMemo(() => tasks.filter((task) => task.status !== 'closed' && task.status !== 'evidence_submitted'), [tasks])
  const closed = useMemo(() => tasks.filter((task) => task.status === 'closed'), [tasks])

  async function complete(task: any) {
    setBusy(task.id)
    try {
      await api.completeCare({ taskId: task.id, note: 'Completion evidence submitted from operations UI', completedAt: new Date().toISOString() })
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function review(task: any, decision: 'verified' | 'rejected') {
    setBusy(task.id)
    try {
      await api.verifyCare(task.id, {
        decision,
        note: decision === 'verified' ? 'Completion accepted by verifier.' : 'Completion needs additional field work or evidence.',
      })
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      await api.createCare({
        plotId: form.get('plotId') || undefined,
        treeOrganismId: form.get('treeId') || undefined,
        taskType: form.get('taskType'),
        title: form.get('title'),
        description: form.get('description') || undefined,
        priority: form.get('priority'),
        dueAt: form.get('dueAt') ? new Date(String(form.get('dueAt'))).toISOString() : undefined,
      })
      setModal(false)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page-stack">
      <section className="page-header-row">
        <div><h2>Care & issues</h2><p>Open work is submitted and reviewed before closure.</p></div>
        <div className="hero-actions"><button className="button button--primary" onClick={() => { setModalError(''); setModal(true) }}><Plus size={17} /> Care task</button><button className="button button--secondary" onClick={() => void reload()} disabled={loading}><RefreshCw size={16} /> Refresh</button></div>
      </section>

      {error && <section className="panel" role="alert"><div className="panel-heading"><div><h3>Could not load care work</h3></div><button className="button button--secondary" onClick={() => void reload()} disabled={loading}>Try again</button></div><p style={{ padding: '0 19px 17px', margin: 0 }}>{error}</p></section>}

      {awaitingReview.length > 0 && <section className="panel">
        <div className="panel-heading"><div><h3>{awaitingReview.length} completion{awaitingReview.length === 1 ? '' : 's'} awaiting review</h3></div><ShieldCheck size={20} /></div>
        <div className="campaign-list">{awaitingReview.map((task) => <article className="campaign-row" key={task.id}>
          <div><div className="campaign-row__heading"><div><strong>{task.title}</strong><span>{task.tree_code || task.plot_name || 'Plantation'} · {task.task_type}{task.completed_at ? ` · submitted ${formatDateTime(task.completed_at)}` : ''}</span></div><StatusPill value={task.priority} compact /></div>{task.description && <p>{task.description}</p>}</div>
          <div className="hero-actions"><StatusPill value={task.status} compact /><button disabled={busy === task.id} className="button button--primary button--small" onClick={() => void review(task, 'verified')}><ShieldCheck size={15} /> Accept</button><button disabled={busy === task.id} className="button button--secondary button--small" onClick={() => void review(task, 'rejected')}><RotateCcw size={15} /> Return</button></div>
        </article>)}</div>
      </section>}

      <section className="panel">
        <div className="panel-heading"><div><h3>{loading ? 'Loading tasks…' : error ? '—' : `${open.length} task${open.length === 1 ? '' : 's'}`}</h3></div><Wrench size={20} /></div>
        {loading ? <div className="empty-state" role="status"><span>Loading open work…</span></div> : open.length === 0 ? <div className="empty-state"><CheckCircle2 size={30} /><strong>No open care work</strong><span>New issues and scheduled tasks will appear here.</span></div> : <div className="campaign-list">{open.map((task) => <article className="campaign-row" key={task.id}>
          <div><div className="campaign-row__heading"><div><strong>{task.title}</strong><span>{task.tree_code || task.plot_name || 'Plantation'} · {task.task_type}{task.due_at ? ` · due ${formatDateTime(task.due_at)}` : ''}</span></div><StatusPill value={task.priority} compact /></div>{task.description && <p>{task.description}</p>}</div>
          <div className="hero-actions"><StatusPill value={task.status} compact /><button disabled={busy === task.id} className="button button--primary button--small" onClick={() => void complete(task)}><CheckCircle2 size={15} /> Submit completion</button></div>
        </article>)}</div>}
      </section>

      {closed.length > 0 && <section className="panel"><div className="panel-heading"><div><h3>Verified completions</h3></div><Clock3 size={20} /></div><div className="campaign-list">{closed.slice(0, 15).map((task) => <article className="campaign-row" key={task.id}><div><strong>{task.title}</strong><span>{task.tree_code || task.plot_name || 'Plantation'}{task.verified_at ? ` · verified ${formatDateTime(task.verified_at)}` : ''}</span></div><StatusPill value="closed" compact /></article>)}</div></section>}

      {modal && <Modal label="Create care task" onClose={() => { if (!busy) setModal(false) }}>
        <h2>Create care task</h2>
        {modalError && <div className="inline-alert inline-alert--danger" role="alert"><div><strong>Could not create this task</strong><span>{modalError}</span></div></div>}
        <form onSubmit={create}>
          <label className="field-label"><span>Tree organism ID <em>optional</em></span><input name="treeId" placeholder="tree_…" /></label>
          <label className="field-label"><span>Or plot</span><select name="plotId"><option value="">Choose plot</option>{plots.map((plot) => <option value={plot.id} key={plot.id}>{plot.name} · {plot.site_name}</option>)}</select></label>
          <label className="field-label"><span>Task type</span><select name="taskType"><option value="watering">Watering</option><option value="inspection">Inspection</option><option value="guard_repair">Guard repair</option><option value="weeding">Weeding</option><option value="replacement">Replacement</option><option value="survival_check">Survival check</option></select></label>
          <label className="field-label"><span>Title</span><input name="title" required /></label>
          <label className="field-label"><span>Description <em>optional</em></span><textarea name="description" rows={3} /></label>
          <label className="field-label"><span>Priority</span><select name="priority"><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option><option value="low">Low</option></select></label>
          <label className="field-label"><span>Due <em>optional</em></span><input name="dueAt" type="datetime-local" /></label>
          <button disabled={Boolean(busy)} className="button button--primary button--wide">{busy ? 'Creating…' : 'Create task'}</button>
        </form>
        <button type="button" className="button button--secondary button--wide" onClick={() => setModal(false)} disabled={Boolean(busy)}>Cancel</button>
      </Modal>}
    </div>
  )
}
